import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Mail, Lock, User, Users, Building2, Search, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [leaderId, setLeaderId] = useState('');
  const [teamName, setTeamName] = useState('');
  const [leaders, setLeaders] = useState([]);
  const [filteredLeaders, setFilteredLeaders] = useState([]);
  const [leaderSearch, setLeaderSearch] = useState('');
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [showLeaderDropdown, setShowLeaderDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingLeaders, setLoadingLeaders] = useState(true);
  const [registrationType, setRegistrationType] = useState('member'); // 'member' or 'leader'
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeaders();
  }, []);

  useEffect(() => {
    // Filter leaders based on search (by email or name)
    if (leaderSearch.trim()) {
      const search = leaderSearch.toLowerCase();
      const filtered = leaders.filter(leader => 
        leader.email?.toLowerCase().includes(search) ||
        leader.first_name?.toLowerCase().includes(search) ||
        leader.last_name?.toLowerCase().includes(search) ||
        leader.team_name?.toLowerCase().includes(search)
      );
      setFilteredLeaders(filtered);
      setShowLeaderDropdown(true);
    } else {
      setFilteredLeaders(leaders);
      setShowLeaderDropdown(false);
    }
  }, [leaderSearch, leaders]);

  const fetchLeaders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leaders`);
      setLeaders(response.data);
      setFilteredLeaders(response.data);
    } catch (error) {
      // Silent fail - leader dropdown will be empty
    } finally {
      setLoadingLeaders(false);
    }
  };

  const handleSelectLeader = (leader) => {
    setSelectedLeader(leader);
    setLeaderId(leader.id);
    setLeaderSearch(leader.email || `${leader.first_name} ${leader.last_name}`);
    setShowLeaderDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (registrationType === 'leader' && !teamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }

    if (registrationType === 'member' && !leaderId) {
      toast.error('Please select a team to join');
      return;
    }
    
    setLoading(true);

    const result = await register(
      email, 
      password, 
      firstName, 
      lastName, 
      registrationType === 'member' ? leaderId : null,
      registrationType === 'leader' ? teamName : null
    );
    
    if (result.success) {
      toast.success(result.message);
      if (registrationType === 'leader') {
        navigate('/login', { state: { message: 'Team created! You can now log in.' } });
      } else {
        navigate('/login', { state: { message: 'Registration successful! Your account is pending approval from your leader.' } });
      }
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center bg-cover bg-right bg-fixed relative"
      style={{ backgroundImage: `url('https://images.pexels.com/photos/17796162/pexels-photo-17796162.jpeg?auto=compress&cs=tinysrgb&w=1920')` }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
      
      {/* Left side - Register content */}
      <div className="relative z-10 w-full lg:w-1/2 flex flex-col items-center justify-center py-8 px-4">
        <Card data-testid="register-card" className="w-full max-w-md p-8 max-h-[90vh] overflow-y-auto">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center mb-3">
              <img src="/peacock-logo.png" alt="VRMX" className="h-14 w-14 object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-[#333333] mb-1">Create Account</h1>
            <p className="text-gray-600 text-sm">Join VRMX today</p>
          </div>

          {/* Registration Type Toggle */}
          <div className="mb-5">
            <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-50">
              <button
                type="button"
                onClick={() => setRegistrationType('member')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  registrationType === 'member'
                    ? 'bg-white shadow text-[#E31837]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Users className="h-4 w-4 inline-block mr-1" />
                Join a Team
              </button>
              <button
                type="button"
                onClick={() => setRegistrationType('leader')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  registrationType === 'leader'
                    ? 'bg-white shadow text-[#E31837]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Building2 className="h-4 w-4 inline-block mr-1" />
                Create Team
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName" className="text-sm">First Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="firstName"
                    data-testid="register-firstname-input"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    required
                    className="pl-10 h-9"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="lastName" className="text-sm">Last Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="lastName"
                    data-testid="register-lastname-input"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    required
                    className="pl-10 h-9"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  data-testid="register-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  required
                  className="pl-10 h-9"
                />
              </div>
            </div>

            {registrationType === 'member' ? (
              <div className="relative">
                <Label htmlFor="leaderSearch" className="text-sm">Find Your Team (Search by Email)</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="leaderSearch"
                    data-testid="register-leader-search"
                    type="text"
                    value={leaderSearch}
                    onChange={(e) => {
                      setLeaderSearch(e.target.value);
                      setSelectedLeader(null);
                      setLeaderId('');
                    }}
                    onFocus={() => setShowLeaderDropdown(true)}
                    placeholder={loadingLeaders ? "Loading teams..." : "Type leader's email or name..."}
                    className="pl-10 h-9"
                  />
                </div>
                
                {/* Dropdown for matching leaders */}
                {showLeaderDropdown && filteredLeaders.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredLeaders.map((leader) => (
                      <button
                        key={leader.id}
                        type="button"
                        onClick={() => handleSelectLeader(leader)}
                        className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${
                          selectedLeader?.id === leader.id ? 'bg-green-50' : ''
                        }`}
                      >
                        <div>
                          <p className="font-medium text-sm">{leader.team_name || `${leader.first_name}'s Team`}</p>
                          <p className="text-xs text-gray-500">{leader.email} • {leader.first_name} {leader.last_name}</p>
                        </div>
                        {selectedLeader?.id === leader.id && (
                          <Check className="h-4 w-4 text-green-600" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
                
                {showLeaderDropdown && filteredLeaders.length === 0 && leaderSearch && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-center text-gray-500 text-sm">
                    No teams found matching "{leaderSearch}"
                  </div>
                )}

                {selectedLeader && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Joining: {selectedLeader.team_name || `${selectedLeader.first_name}'s Team`}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <Label htmlFor="teamName" className="text-sm">Team Name</Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="teamName"
                    data-testid="register-team-name-input"
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="My Awesome Team"
                    required={registrationType === 'leader'}
                    className="pl-10 h-9"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Your team members will see this name when joining</p>
              </div>
            )}

            <div>
              <Label htmlFor="password" className="text-sm">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  data-testid="register-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  required
                  className="pl-10 h-9"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-sm">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  data-testid="register-confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className="pl-10 h-9"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="register-submit-button"
              className="w-full bg-[#E31837] hover:bg-[#C4122C] text-white mt-4"
              disabled={loading}
            >
              {loading ? 'Creating account...' : registrationType === 'leader' ? 'Create Team' : 'Join Team'}
            </Button>
          </form>

          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              {registrationType === 'leader' ? (
                <><strong>Team Leaders:</strong> You can start using the platform immediately after registration.</>
              ) : (
                <><strong>Note:</strong> After registration, your team leader will need to approve your account before you can log in.</>
              )}
            </p>
          </div>

          <div className="mt-4 text-center">
            <p className="text-gray-600 text-sm">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-[#E31837] hover:underline font-medium"
                data-testid="login-link"
              >
                Log in
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
